import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import authService from '../../services/authService';

/**
 * Email Verification Page
 * Verifies user email using token from verification link.
 *
 * Two flows live here:
 *   - On mount, hit POST /auth/verify-email/:token with the token from the URL.
 *     Successful → redirect to /login. Failed (expired/used/invalid) →
 *     show the error state with a resend form.
 *   - The resend form takes an email and calls POST /auth/resend-verification.
 *     The backend always returns success (to prevent email enumeration), so
 *     the UI does too — we just tell the user to check their inbox.
 */

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);

  const verifyEmail = useCallback(async () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Verification token missing from the URL.');
      return;
    }

    try {
      const response = await authService.verifyEmail(token);
      if (response?.success) {
        setStatus('success');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setStatus('error');
        setErrorMessage(response?.error || 'Verification link is invalid or has expired.');
      }
    } catch (error) {
      setStatus('error');
      const apiError = error?.response?.data?.error || error?.message;
      setErrorMessage(apiError || 'Verification link is invalid or has expired.');
    }
  }, [navigate, token]);

  useEffect(() => {
    verifyEmail();
  }, [verifyEmail]);

  const resendVerification = async (e) => {
    e?.preventDefault?.();
    if (!resendEmail.trim()) {
      toast.error('Enter the email you registered with');
      return;
    }
    setResending(true);
    try {
      await authService.resendEmailVerification(resendEmail.trim());
      // Backend gives the same success response whether or not the address
      // is registered (anti-enumeration). Mirror that in the UI.
      toast.success('If that account exists and is unverified, a new link is on its way.');
      setResendEmail('');
    } catch (error) {
      const apiError = error?.response?.data?.error || error?.message;
      toast.error(apiError || 'Could not resend verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card padding="lg" className="shadow-lg text-center">
          {/* Verifying State */}
          {status === 'verifying' && (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6"
              >
                <svg className="w-10 h-10 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </motion.div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-3">Verifying Your Email</h2>
              <p className="text-neutral-600">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6"
              >
                <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </motion.div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-3">Email Verified!</h2>
              <p className="text-neutral-600 mb-6">
                Your email has been successfully verified. You can now access all features of your account.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-700">
                  Redirecting to login in 3 seconds...
                </p>
              </div>
              <Link to="/login">
                <Button variant="primary" fullWidth>
                  Go to Login
                </Button>
              </Link>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6"
              >
                <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </motion.div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-3">Verification Failed</h2>
              <p className="text-neutral-600 mb-6">{errorMessage}</p>

              <form onSubmit={resendVerification} className="space-y-3 text-left">
                <label htmlFor="resend-email" className="block text-sm font-medium text-neutral-700">
                  Enter your email to receive a new link
                </label>
                <input
                  id="resend-email"
                  type="email"
                  required
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white"
                />
                <Button type="submit" variant="primary" fullWidth loading={resending} disabled={resending}>
                  {resending ? 'Sending…' : 'Resend Verification Email'}
                </Button>
                <Link to="/login">
                  <Button type="button" variant="ghost" fullWidth>
                    Back to Login
                  </Button>
                </Link>
              </form>

              <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">Common Issues</p>
                    <ul className="text-amber-700 space-y-1 text-left">
                      <li>• Verification link expired (valid for 24 hours)</li>
                      <li>• Link already used</li>
                      <li>• Email already verified</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default EmailVerificationPage;
